import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { EnvironmentProvider } from './context/EnvironmentContext';
import { NotificationProvider } from './context/NotificationContext';
import ProtectedRoute from './components/auth/ProtectedRoute/ProtectedRoute';
import Header from './components/layout/Header/Header';
import EnvironmentBanner from './components/layout/EnvironmentBanner/EnvironmentBanner';
import ProductionWarning from './components/safety/ProductionWarning/ProductionWarning';
import LoginPage from './pages/LoginPage/LoginPage';
import CompaniesPage from './pages/CompaniesPage/CompaniesPage';
import TechnologiesPage from './pages/TechnologiesPage/TechnologiesPage';
import TechnologyMapPage from './pages/TechnologyMapPage/TechnologyMapPage';
import TherapeuticsPage from './pages/TherapeuticsPage/TherapeuticsPage';
import DiseasesPage from './pages/DiseasesPage/DiseasesPage';
import UserManagementPage from './pages/UserManagementPage/UserManagementPage';
import ScheduledJobsPage from './pages/ScheduledJobsPage/ScheduledJobsPage';
import BulkCompanyImport from './pages/BulkCompanyImport/BulkCompanyImport';
import ContentCompletionPage from './pages/ContentCompletionPage/ContentCompletionPage';
import CompletionDashboardPage from './pages/CompletionDashboardPage/CompletionDashboardPage';
import MilestoneDashboardPage from './pages/MilestoneDashboardPage/MilestoneDashboardPage';
import CompanyPipelinesPage from './pages/CompanyPipelinesPage/CompanyPipelinesPage';
import PipelineIntelligencePage from './pages/PipelineIntelligencePage/PipelineIntelligencePage';

import DocumentIntelligencePage from './pages/DocumentIntelligencePage/DocumentIntelligencePage';
import LlmJobsPage from './pages/LlmJobsPage/LlmJobsPage';
import './styles/globals.css';

// Layout wrapper for authenticated pages
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <EnvironmentBanner />
      <Header />
      <main className="flex-1">
        {children}
      </main>
    </div>
  );
};

function App() {
  return (
    <Router
      basename=""
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AuthProvider>
        <EnvironmentProvider>
          <NotificationProvider>
            <ProductionWarning />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/companies"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <CompaniesPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/technologies"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <TechnologiesPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/technologies/map"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <TechnologyMapPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/therapeutics"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <TherapeuticsPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/diseases"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <DiseasesPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bulk-import/companies"
                element={
                  <ProtectedRoute requireEditorOrAdmin>
                    <AppLayout>
                      <BulkCompanyImport />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/completion-dashboard"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <CompletionDashboardPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/milestone-dashboard"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <MilestoneDashboardPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/company-pipelines"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <CompanyPipelinesPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pipeline-intelligence"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <PipelineIntelligencePage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/document-intelligence"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <DocumentIntelligencePage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/content-completion"
                element={
                  <ProtectedRoute>
                    <AppLayout>
                      <ContentCompletionPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute requireAdmin>
                    <AppLayout>
                      <UserManagementPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/llm-jobs"
                element={
                  <ProtectedRoute requireEditorOrAdmin>
                    <AppLayout>
                      <LlmJobsPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/scheduled-jobs"
                element={
                  <ProtectedRoute requireEditorOrAdmin>
                    <AppLayout>
                      <ScheduledJobsPage />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Navigate to="/companies" replace />} />
            </Routes>
          </NotificationProvider>
        </EnvironmentProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
